import React, { useCallback, useMemo, useState, useEffect } from "react";

// Import images
import m1 from "../../../../../Assets/Images/defaultMedia1.jpeg";
import { decryptMessage } from "../../../../../Components/EncryptDecryptMessage/EncryptDecryptMessage";
import { FadeLoader } from "react-spinners";
import InfiniteScroll from "../../../../../Components/InfiniteScroll/InfiniteScroll";

export default function Links({
  linkList,
  isMediaLoading,
  getMediaList,
  linkCurrentPage,
  linkTotalPage,
  secretKey,
  chatConversationId,
}) {
  const [decryptedLinkList, setDecryptedLinkList] = useState([]);
  const getPreviewLink = useCallback((url) => {
    return `https://www.google.com/s2/favicons?sz=64&domain_url=${url}`;
  }, []);

  const extractLink = useCallback((message) => {
    const urlRegex = /(?:https?:\/\/)?(?:www\.)?[\w-]+(\.[\w-]+)+([\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])?/g;
    const urls = message.match(urlRegex);
    return urls ? urls[0] : null;
  }, []);

  const getUrlBaseDomain = useCallback((link) => {
    try {
      // Add https:// if no protocol is present
      const urlWithProtocol = link.startsWith("http")
        ? link
        : `https://${link}`;
      const url = new URL(urlWithProtocol);
      return url.hostname.replace("www.", "");
    } catch (error) {
      console.error("Invalid URL:", error);
      return link;
    }
  }, []);

  useEffect(() => {
    getMediaList("LINK");
  }, [chatConversationId]);

  const getMessages = useCallback(async () => {
    try {
      if (linkList.length <= 0 || !secretKey) return [];
      const msgs = linkList.map(async (msg) => {
        const decryptedText = await decryptMessage(
          msg?.cipherText,
          msg.iv,
          secretKey
        );
        const link = extractLink(decryptedText);
        const previewLink = getPreviewLink(link);
        return {
          ...msg,
          message: decryptedText,
          link: link,
          previewLink: previewLink,
        };
      });
      const dt = await Promise.all(msgs);
      setDecryptedLinkList(dt);
    } catch (error) {
      console.log(error);
    }
  }, [linkList, secretKey]);

  useEffect(() => {
    getMessages();
  }, [getMessages]);

  const groupedData = useMemo(() => {
    const groupedData = {};
    decryptedLinkList.forEach((item) => {
      const date = new Date(item.sentTime).toLocaleDateString("en-US", {
        timeZone: "Asia/Kolkata",
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });
      groupedData[date] = groupedData[date] || [];
      groupedData[date].push(item);
    });
    return groupedData;
  }, [decryptedLinkList]);

  const fetchMoreLinks = async () => {
    if (linkCurrentPage < linkTotalPage) {
      await getMediaList("LINK", linkCurrentPage + 1);
    }
  };

  return (
    <section>
      {/* Today Section */}
      {isMediaLoading ? (
        <div className="flex justify-center items-center h-full mt-5">
          <FadeLoader color="#7104F8" size={15} margin={2} />
        </div>
      ) : linkList.length <= 0 ? (
        <div className="flex justify-center items-center h-full">
          <p className="text-gray-500">No links found</p>
        </div>
      ) : (
        <InfiniteScroll
          dataLength={decryptedLinkList.length}
          next={fetchMoreLinks}
          hasMore={linkCurrentPage < linkTotalPage}
          loader={
            <p className="text-gray-500 text-center">Loading more links...</p>
          }
        >
          {Object.entries(groupedData)
            .sort(([dateA], [dateB]) => new Date(dateB) - new Date(dateA))
            .map(([date, links]) => (
              <div key={date}>
                <label className="text-sm font-semibold">{date}</label>
                <div className="flex flex-col gap-3 mt-2">
                  {links.map((link) => (
                    <a href={link.link} target="_blank" rel="noreferrer">
                      <div className="flex items-start gap-3 bg-[#F0F0F3] rounded-xl p-2 w-full max-w-md">
                        <img
                          src={link.previewLink}
                          alt="media"
                          className="min-w-[64px] min-h-[64px] max-w-[64px] max-h-[64px] rounded-lg object-cover"
                        />
                        <div className="flex-grow-1">
                          <h3 className="font-semibold text-sm break-words line-clamp-2 w-[290px]">
                            {link.link}
                          </h3>
                          <p className="text-xs text-gray-500 mt-1 w-full break-words">
                            {getUrlBaseDomain(link.link)}
                          </p>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            ))}
        </InfiniteScroll>
      )}
    </section>
  );
}
